import {useState, useEffect, useRef} from "react";
import {
  ShieldCheck,
  LogOut,
  ChevronRight,
  Phone,
  LayoutDashboard,
  User,
} from "lucide-react";
import VideoDashboard from "./components/VideoDashboard";
import AdminDashboard from "./components/AdminDashboard";
import Account from "./components/Account";
import {v4 as uuidv4} from "uuid";
import {parsePhoneNumber} from "libphonenumber-js";

// Constants
const BASE_URL = process.env.REACT_APP_AWS_USER_ENDPOINT;
const AUTH_HEADER = `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`;
const HEADERS = {
  "Content-Type": "application/json",
  Authorization: AUTH_HEADER,
};

// Modern Loader Component
const OrbitLoader = () => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="relative h-12 w-12">
      <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
      <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
    <p className="text-sm font-medium text-slate-500 animate-pulse">
      Loading...
    </p>
  </div>
);

// Helper Component for UI consistency
const InputGroup = ({
  id,
  type = "text",
  placeholder,
  value,
  onChange,
  onKeyDown,
  icon,
}) => (
  <div className="relative group w-full">
    {icon && (
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
        {icon}
      </div>
    )}
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className={`w-full py-3.5 ${icon ? "pl-11" : "px-4"} pr-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition-all text-slate-800 placeholder:text-slate-400`}
    />
  </div>
);

const App = () => {
  // State Groups
  const [signUpData, setSignUpData] = useState({
    firstname: "",
    lastname: "",
    phone: "",
  });
  const [loginData, setLoginData] = useState({phone: ""});
  const [confirmData, setConfirmData] = useState({
    phone: "",
    confirmationCode: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Updated navigation state: "dashboard", "admin", "account"
  const [currentView, setCurrentView] = useState("dashboard");

  const [resendTimer, setResendTimer] = useState(0);
  const [loginState, setLoginState] = useState({
    state: "login",
    message: null,
    user: null,
  });

  const dropdownRef = useRef(null);

  // Logic Functions
  const apiFetch = async (endpoint, options = {}) => {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers: {...HEADERS, ...options.headers},
    });
    if (!response.ok)
      throw new Error(
        options.errorMsg || `HTTP error! status: ${response.status}`,
      );
    return response.json();
  };

  const convertToE164 = (phoneNumberString, defaultCountryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        phoneNumberString,
        defaultCountryCode,
      );
      return phoneNumber && phoneNumber.isValid()
        ? phoneNumber.format("E.164")
        : phoneNumberString;
    } catch (error) {
      return phoneNumberString;
    }
  };

  const handleInputChange = (setter) => (e) => {
    const {id, value} = e.target;
    setter((prev) => ({...prev, [id]: value}));
  };

  // Auth Handlers
  const handleSignUp = async () => {
    setIsActionLoading(true);
    const {phone, firstname, lastname} = signUpData;
    const e164Phone = convertToE164(phone, "US");
    try {
      const {result, data} = await apiFetch(`/get-contact/id/${e164Phone}`);

      if (result === "success" && data?.length > 0) {
        return setLoginState({
          state: "signup",
          message: "Account already exists.",
          message_type: "error",
          user: null,
        });
      }

      await apiFetch("/update-contact/activity", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: e164Phone,
          customer_firstname_u: firstname,
          customer_lastname_u: lastname,
          timestamp_u: new Date().toISOString(),
        }),
      });
      await apiFetch("/update-contact/otp", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: e164Phone,
          timestamp_u: new Date().toISOString(),
        }),
      });

      setLoginState({
        state: "confirm",
        message: `Verification code sent to ${e164Phone}.`,
        message_type: "success",
        user: null,
      });
      setConfirmData({phone: e164Phone, confirmationCode: ""});
      setResendTimer(60);
    } catch (error) {
      console.log(error);
      await apiFetch(`/delete-contact/id/${e164Phone}`, {
        method: "DELETE",
      });
      setLoginState({
        state: "signup",
        message: "Unable to process sign up.",
        message_type: "error",
        user: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    const e164Phone = convertToE164(loginData.phone, "US");
    try {
      const {result, data} = await apiFetch(`/get-contact/id/${e164Phone}`);
      const lastRecordedTimestamp = data?.[0]?.timestamp
        ? new Date(data[0].timestamp)
        : null;
      const now = new Date();
      const isMoreThan100s = lastRecordedTimestamp
        ? now - lastRecordedTimestamp > 100000
        : true;

      if (result !== "success" || !data?.length) {
        setLoginState({
          state: "signup",
          message: "Account not found. Please sign up.",
          message_type: "error",
          user: null,
        });
        return;
      } else if (
        result === "success" &&
        data[0].session_id &&
        !isMoreThan100s
      ) {
        setLoginState({
          state: "login",
          message:
            "You are already logged in on another device. Please log out first.",
          message_type: "error",
          user: null,
        });
        return;
      }
      await apiFetch("/update-contact/otp", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: e164Phone,
          timestamp_u: new Date().toISOString(),
        }),
      });
      setLoginState({
        state: "otp_verify",
        message: `OTP sent to ${e164Phone}!`,
        message_type: "success",
        user: null,
      });
      setConfirmData({phone: e164Phone, confirmationCode: ""});
      setResendTimer(60);
    } catch (error) {
      setLoginState({
        state: "login",
        message: "Error sending OTP.",
        message_type: "error",
        user: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setIsActionLoading(true);
    try {
      const e164Phone = convertToE164(
        loginData.phone || confirmData.phone,
        "US",
      );
      const {result, data} = await apiFetch(`/get-contact/id/${e164Phone}`);

      if (
        result === "success" &&
        data[0]?.otp === confirmData.confirmationCode
      ) {
        const sessionId = uuidv4();
        await apiFetch("/update-contact/session", {
          method: "PATCH",
          body: JSON.stringify({
            customer_id_u: e164Phone,
            session_id_u: sessionId,
            timestamp_u: new Date().toISOString(),
          }),
        });

        const loggedInUser = {
          given_name: data[0].customer_firstname,
          family_name: data[0].customer_lastname,
          customer_id: e164Phone,
          session_id: sessionId,
        };

        setLoginState({state: "logged_in", user: loggedInUser, message: null});
        sessionStorage.setItem("userSession", JSON.stringify(loggedInUser));
      } else {
        setLoginState({
          ...loginState,
          message: "Incorrect code.",
          message_type: "error",
        });
      }
    } catch (error) {
      setLoginState({
        ...loginState,
        message: "Verification failed.",
        message_type: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await apiFetch("/update-contact/session", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: loginState.user?.customer_id,
          session_id_u: null,
          timestamp_u: new Date().toISOString(),
        }),
      });
      setLoginState({state: "login", message: null, user: null});
      sessionStorage.removeItem("userSession");
      setCurrentView("dashboard");
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Effects
  //   useEffect(() => {
  //     const handleClickOutside = (e) => {
  //       if (dropdownRef.current && !dropdownRef.current.contains(e.target))
  //         setIsDropdownOpen(false);
  //     };
  //     document.addEventListener("mousedown", handleClickOutside);
  //     return () => document.removeEventListener("mousedown", handleClickOutside);
  //   }, []);

  useEffect(() => {
    const userSession = sessionStorage.getItem("userSession");
    if (userSession)
      setLoginState({state: "logged_in", user: JSON.parse(userSession)});
  }, []);

  useEffect(() => {
    let interval;
    if (resendTimer > 0)
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    const userSession = sessionStorage.getItem("userSession");
    const sessionBoundId = sessionStorage.getItem("tab_bound_id");

    // Check if this is a duplicated tab
    // 1. If we have a session but window.name is empty, it's a duplicate.
    // 2. If window.name doesn't match the ID we bound to the session, it's a duplicate.
    const isDuplicate =
      userSession && (!window.name || window.name !== sessionBoundId);

    if (isDuplicate) {
      // Clear storage and force re-login
      sessionStorage.clear();
      setLoginState({
        state: "login",
        message: "Session security: Please re-login for this tab.",
        message_type: "error",
      });
    } else if (userSession) {
      // Valid session
      setLoginState({state: "logged_in", user: JSON.parse(userSession)});
    } else {
      // Fresh tab, no session - assign a unique ID to this tab's name
      const newTabId = uuidv4();
      window.name = newTabId;
      sessionStorage.setItem("tab_bound_id", newTabId);
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isActionLoading) {
      if (loginState.state === "login") handleLogin();
      if (loginState.state === "signup") handleSignUp();
      if (["otp_verify", "confirm"].includes(loginState.state))
        handleVerifyOTP();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#F8FAFC] antialiased">
      {/* Fixed Navbar */}
      <header className="flex-shrink-0 flex bg-white/80 backdrop-blur-md w-full h-16 items-center justify-between px-6 border-b border-slate-200 sticky top-0 z-50">
        <div className="flex">
          <img
            src="./images/ccx_logo.png"
            className="pointer-events-none p-4 w-40"
            alt="Logo"
          />
        </div>

        {loginState.state === "logged_in" && loginState.user && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 p-1.5 pl-3 rounded-full border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all"
            >
              <span className="text-xs font-semibold text-slate-700 hidden sm:block">
                {loginState.user.given_name}
              </span>
              <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold">
                {loginState.user.given_name[0]}
              </div>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 animate-in fade-in zoom-in duration-200">
                <div className="px-4 py-3 border-b border-slate-50">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    User ID
                  </p>
                  <p className="text-sm text-slate-600 font-medium truncate">
                    {loginState.user.customer_id}
                  </p>
                </div>

                {/* Navigation Options */}
                {/* Account is now first after the ID header */}
                <button
                  onClick={() => {
                    setCurrentView("account");
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${currentView === "account" ? "text-indigo-600 bg-indigo-50 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <User
                    size={18}
                    className={
                      currentView === "account"
                        ? "text-indigo-500"
                        : "text-slate-400"
                    }
                  />
                  Account
                </button>

                {/* Second button toggles between Dashboard and Admin */}
                <button
                  onClick={() => {
                    setCurrentView("dashboard");
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${currentView === "dashboard" ? "text-indigo-600 bg-indigo-50 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <LayoutDashboard size={18} className="text-slate-400" />
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    setCurrentView("admin");
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${currentView === "admin" ? "text-indigo-600 bg-indigo-50 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <ShieldCheck
                    size={18}
                    className={
                      currentView === "admin"
                        ? "text-indigo-500"
                        : "text-slate-400"
                    }
                  />
                  Admin
                </button>

                <div className="h-px bg-slate-100 my-1"></div>

                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-3 font-medium"
                >
                  <LogOut size={18} /> Log Out
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Main Container */}
      <main
        className={`flex-1 overflow-y-auto flex flex-col ${
          loginState.state === "logged_in"
            ? "items-stretch"
            : "items-center justify-center p-6"
        }`}
      >
        {isLoading ? (
          <div className="m-auto">
            <OrbitLoader />
          </div>
        ) : (
          <>
            {/* Login Flow */}
            {["signup", "login", "otp_verify", "confirm"].includes(
              loginState.state,
            ) && (
              <div className="w-full max-w-md bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-10 border border-slate-100 transition-all">
                <div className="mb-8 text-center">
                  <h2 className="text-2xl font-bold text-slate-800">
                    {loginState.state === "login" && "Welcome Back"}
                    {loginState.state === "signup" && "Create Account"}
                    {["otp_verify", "confirm"].includes(loginState.state) &&
                      "Verify Phone"}
                  </h2>
                </div>

                {loginState.message && (
                  <div
                    className={`mb-6 p-3 rounded-xl text-xs font-medium border flex items-center gap-2 ${
                      loginState.message_type === "error"
                        ? "bg-rose-50 text-rose-700 border-rose-100"
                        : "bg-emerald-50 text-emerald-700 border-emerald-100"
                    }`}
                  >
                    {loginState.message}
                  </div>
                )}

                <div className="space-y-4">
                  {loginState.state === "signup" && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <InputGroup
                          id="firstname"
                          placeholder="First Name"
                          value={signUpData.firstname}
                          onChange={handleInputChange(setSignUpData)}
                          onKeyDown={handleKeyDown}
                        />
                        <InputGroup
                          id="lastname"
                          placeholder="Last Name"
                          value={signUpData.lastname}
                          onChange={handleInputChange(setSignUpData)}
                          onKeyDown={handleKeyDown}
                        />
                      </div>
                      <InputGroup
                        id="phone"
                        type="tel"
                        icon={<Phone size={18} />}
                        placeholder="Phone Number"
                        value={signUpData.phone}
                        onChange={handleInputChange(setSignUpData)}
                        onKeyDown={handleKeyDown}
                      />
                    </>
                  )}

                  {loginState.state === "login" && (
                    <InputGroup
                      id="phone"
                      type="tel"
                      icon={<Phone size={18} />}
                      placeholder="Phone Number"
                      value={loginData.phone}
                      onChange={handleInputChange(setLoginData)}
                      onKeyDown={handleKeyDown}
                    />
                  )}

                  {["otp_verify", "confirm"].includes(loginState.state) && (
                    <div className="space-y-4">
                      <input
                        id="confirmationCode"
                        className="w-full text-center text-3xl font-bold tracking-[0.5em] py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl outline-none transition-all"
                        placeholder="••••••"
                        maxLength={6}
                        value={confirmData.confirmationCode}
                        onChange={handleInputChange(setConfirmData)}
                        onKeyDown={handleKeyDown}
                      />
                      <div className="flex justify-between px-1">
                        <button
                          disabled={resendTimer > 0}
                          onClick={() =>
                            loginState.state === "otp_verify"
                              ? handleLogin()
                              : handleSignUp()
                          }
                          className="text-xs font-bold text-indigo-600 disabled:text-slate-400"
                        >
                          {resendTimer > 0
                            ? `Resend in ${resendTimer}s`
                            : "Resend Code"}
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={
                      loginState.state === "login"
                        ? handleLogin
                        : loginState.state === "signup"
                          ? handleSignUp
                          : handleVerifyOTP
                    }
                    disabled={isActionLoading}
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 text-white rounded-2xl font-bold shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2"
                  >
                    {isActionLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      "Continue"
                    )}
                    {!isActionLoading && <ChevronRight size={18} />}
                  </button>

                  <div className="pt-4 text-center">
                    <button
                      onClick={() =>
                        setLoginState({
                          state:
                            loginState.state === "login" ? "signup" : "login",
                          message: null,
                        })
                      }
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      {loginState.state === "login"
                        ? "New here? Create an account"
                        : "Back to login"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Application Views */}
            {loginState.state === "logged_in" && (
              <div className="w-full min-h-full animate-in fade-in duration-500">
                {currentView === "admin" && <AdminDashboard />}
                {currentView === "account" && (
                  <Account loggedInUser={loginState.user} />
                )}
                {currentView === "dashboard" && (
                  <VideoDashboard
                    user={loginState.user}
                    handleLogout={handleSignOut}
                  />
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default App;
